<template>
  <div class="fu-drtab" data-test="fu-drawer-family">
    <section v-for="g in groups" :key="g.key" :data-test="`fu-drawer-family-${g.key}`">
      <div class="fu-drtab__title">{{ g.title }}<span>{{ g.items.length }}</span></div>
      <p v-if="!g.items.length" class="fu-drtab__none">{{ g.empty }}</p>
      <div v-for="(it, i) in g.items" :key="i" class="fu-drtab__item">
        <span class="fu-drtab__avatar" aria-hidden="true">{{ it.initials }}</span>
        <div class="fu-drtab__main">
          <div class="fu-drtab__head">
            <button
              v-if="it.patNum !== null"
              type="button"
              class="fu-drtab__name"
              data-test="fu-drawer-family-open"
              @click="$emit('open-patient', it.patNum)"
            >{{ it.name }}</button>
            <span v-else class="fu-drtab__item-title">{{ it.name }}</span>
            <span v-if="it.light" class="fu-drtab__light">{{ it.light }}</span>
            <span v-if="it.badge" class="fu-drtab__badge" :class="`fu-drtab__badge--${it.badgeTone}`">{{ it.badge }}</span>
          </div>
          <div v-if="it.meta" class="fu-drtab__meta">{{ it.meta }}</div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
/**
 * DrawerFamilyTab — "Family": today's Family Recall Appointments (recall leaves the patient out —
 * drawerTabs), Family Pending Tx and Family Members (recall only) as sections. A family member's name
 * is a button that emits open-patient(PatNum) (today's gotoPatientPage, a new tab — the container's
 * job). Names are shown as loaded — masked at the data boundary, never here. Presentational.
 */
import { initialsOf } from '@/utils/followups/drawer/drawerFacts.js';

const TITLES = { recall: 'Recall', pendingTx: 'Pending treatment', members: 'Members' };
const EMPTY = {
  recall: 'No family recall appointments found.',
  pendingTx: 'No pending treatment for any family members.',
  members: 'No family members linked in Open Dental.',
};

export default {
  name: 'DrawerFamilyTab',
  props: {
    tab: { type: Object, required: true },
    ctx: { type: Object, required: true },
  },
  computed: {
    groups() {
      const c = this.ctx;
      return (this.tab.sections || []).filter(s => s.shown !== false).map((s) => {
        const rows = s.rows || [];
        const items = rows.map((p) => {
          const name = [p.LName, p.FName].filter(Boolean).join(', ');
          const it = {
            name: name || 'Family member',
            initials: initialsOf(name),
            patNum: p.PatNum === undefined || p.PatNum === null ? null : p.PatNum,
            light: '',
            meta: '',
            badge: '',
            badgeTone: 'gray',
          };
          if (s.key === 'recall') {
            it.light = p.Age !== undefined && p.Age !== null ? `${p.Age} yrs` : '';
            if (p.AptDateTime) { it.badge = `Recall booked ${c.formatDate(p.AptDateTime)}`; it.badgeTone = 'green'; }
            else { it.badge = 'Not scheduled'; it.badgeTone = 'amber'; }
            it.meta = [p.DateDue ? `Due ${c.formatDate(p.DateDue)}` : '', p.LastCalled ? `Last called ${c.formatDate(p.LastCalled)}` : '', p.Status || ''].filter(Boolean).join(' · ');
          } else if (s.key === 'pendingTx') {
            it.badge = p.Priority || '';
            it.meta = [p.ProcCode, [p.ToothNum, p.Surf].filter(Boolean).join(' '), p.Descript, p.DateTP ? `planned ${c.formatDate(p.DateTP)}` : ''].filter(Boolean).join(' · ');
          } else {
            const age = c.ageOf(p.Birthdate);
            it.light = age ? `${age} yrs` : '';
            it.meta = [p.HmPhone, p.WirelessPhone].filter(x => x && String(x).trim() !== '').join(' · ');
            it.patNum = null; // today's Family Members table has no link
          }
          return it;
        });
        return { key: s.key, title: TITLES[s.key] || s.legacyTitle, empty: EMPTY[s.key] || 'None.', items };
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './drawer';

.fu-drtab__title { @include fu-dr-section-title; span { color: $fu-text-subtle; } }
.fu-drtab__none { margin: 0; padding: 12px 0; font-size: 13px; color: $fu-text-subtle; border-bottom: 1px solid $fu-border; }
.fu-drtab__item { @include fu-dr-item; }
.fu-drtab__main { flex: 1; min-width: 0; }
.fu-drtab__head { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.fu-drtab__item-title { @include fu-dr-item-title; }
.fu-drtab__meta { @include fu-dr-item-meta; }
.fu-drtab__light { font-size: 12.5px; color: $fu-text-subtle; }

.fu-drtab__avatar {
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $fu-neutral-100;
  color: $fu-neutral-600;
  font-weight: $fu-fw-bold;
  font-size: 12px;
}

.fu-drtab__name {
  @include fu-link-btn;
  font-size: 13.5px;
  font-weight: $fu-fw-bold;
  text-align: left;
}

.fu-drtab__badge { @include fu-pill; }
.fu-drtab__badge--green { background: $fu-success-subtle; color: #0f6b4d; }
.fu-drtab__badge--amber { background: $fu-warning-subtle; color: #7a4d0c; }
</style>
